import { Component, EventEmitter, forwardRef, OnInit, Output } from '@angular/core';
import { Country } from '../../../features/tickets/tickets.model';
import countriesData from "../../../../assets/countries_data/countries.json";
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-mobile-input',
  imports: [CommonModule,FormsModule],
  templateUrl: './mobile-input.component.html',
  styleUrls: ['./mobile-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MobileInputComponent),
      multi: true
    }
  ]
})
export class MobileInputComponent implements OnInit, ControlValueAccessor {
  // --- Form fields & flags ---
  dropdownVisible = false;
  mobileNumber = "";
  disabled = false;

  // --- Country selection & config ---
  countries: Country[] = countriesData;
  selectedCountry: Country =
  this.countries.find((c) => c.name === "India") || this.countries[0];

  @Output() mobileChange = new EventEmitter<{ mobile: string, countryCode: string, countryName: string }>();

  constructor() { }

  ngOnInit(): void { }


  // CVA internal methods
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  // Called when parent writes a value (patchValue/resetValue)
  writeValue(value: any): void {
    if (!value) {
    this.mobileNumber = '';
    return;
  }

  if (typeof value === 'object') {
    // 🔑 assign only the number to mobileNumber
    this.mobileNumber = value.mobile ?? '';

    // update selected country
    const match = this.countries.find(c => c.dial_code === value.countryCode);
    if (match) this.selectedCountry = match;
  } else if (typeof value === 'string') {
    this.mobileNumber = value;
  }

  // update input and emit
  this.emitValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Toggle dropdown visibility.
   * @returns void
   */
  toggleDropdown() {
    this.dropdownVisible = !this.dropdownVisible;
  }

  /**
   * Sanitize and validate mobile input on every keystroke.
   */
  onMobileInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    let value = inputEl.value;

    // Step 1: Remove all non-ASCII digits (block Unicode digits, letters, symbols, spaces, etc.)
    const digits = value.match(/[0-9]/g) || [];

    // Step 2: Enforce max 12 digits
    value = digits.slice(0, 12).join('');

    // Step 2: Remove leading zeros
    value = value.replace(/^0+/, '');

    // Step 3: Update input field and emit value
    inputEl.value = value;

    this.mobileNumber = value;

    this.onChange(this.mobileNumber);
    this.onTouched();
    this.mobileChange.emit({
      mobile: this.mobileNumber,
      countryCode: this.selectedCountry?.dial_code ?? '',
      countryName: this.selectedCountry?.name ?? ''
    });
  }

  /**
   * Prevent non-digit characters and enforce max length.
   */
  onMobileKeypress(event: KeyboardEvent): void {
    if (!/^[0-9]$/.test(event.key) || this.mobileNumber.length >= 12) {
      event.preventDefault();
    }
  }

  /**
   * Toggle country; resets error flags.
   */
  selectCountry(country: Country): void {
    this.selectedCountry = country;
    this.dropdownVisible = false;
    this.emitValue();
  }

  private emitValue() {
    // Update Reactive Form (mobile string only)
    this.onChange(this.mobileNumber);
    this.onTouched();

    // Emit full info for parent component
    this.mobileChange.emit({
      mobile: this.mobileNumber,
      countryCode: this.selectedCountry.dial_code,
      countryName: this.selectedCountry.name
    });
  }

  // 🔑 this method is triggered when parent calls formControl.disable()
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
